/**
 * PriceTileGrid (#2388) — the L3 tile grid for the selected Kategorie. Each
 * tile is a design-system ``MarketplaceTile`` carrying the item name, its last
 * price + unit + as-of date, a trend chip, and the Image-#11 sparkline. Items
 * with no data render as disabled "Keine Daten" tiles (not clickable). All
 * per-tile logic is the pure ``buildTileModel`` — this stays a thin view.
 *
 * **#2694 added a third tile state, and it is not cosmetic.** The grid now
 * paints as soon as the cheap ``/preisspiegel/index`` read says WHICH items have
 * data — before the price read that says WHAT the prices are. In that window a
 * tile has no series yet, and the old rendering would have said "Keine Daten"
 * about an item the page already knows holds 19.000 observations: a flat
 * contradiction of an answer we are holding, and the most damaging one available
 * on this page ("I have no price history" is exactly the wrong thing to tell a
 * farmer mid-negotiation). {@link PriceTileGridProps.prices} splits that window
 * off into a visibly, semantically and audibly distinct skeleton —
 * ``aria-busy``, its own modifier class, and copy that says *loading*, not
 * *absent*.
 *
 * That per-tile scoping is what lets the price read be per-KATEGORIE (#2694
 * stage 2): tiles fill in a Kategorie at a time, and the ones a read did not
 * cover keep an honest empty state instead of spinning forever.
 */
import type { ReactNode } from 'react';
import { TrendingUp, TrendingDown, Minus, Ban, Loader2 } from 'lucide-react';
import { MarketplaceTile } from '@hofsync/design-system';
import { PRICE_BASIS_LABELS, type TrackedItem } from './catalog';
import { benchmarkTileLabel, benchmarkTileLabelFromLevels } from './benchmarkCells';
import type { PriceSeries } from './types';
import { buildTileModel, type TileModel, type TileState } from './tileModel';
import { tileModelFromSummary } from './tileFromSummary';
import type { PreisspiegelTileSummary } from '../../../services/twinService';
import { EstimatedChip } from './estimateMark';
import Sparkline from './PriceSparkline';

const TREND_ICON: Record<TileState, ReactNode> = {
  up: <TrendingUp size={16} />,
  down: <TrendingDown size={16} />,
  flat: <Minus size={16} />,
  empty: <Ban size={16} />,
};

const priceFmt = (value: number): string =>
  new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(value);

/** #2944 — the hover explanation behind the bare-number headline. */
const UNIT_MIXED_HINT =
  'Für diese Position liegen Preise in mehreren Einheiten vor. ' +
  'Eine Überschrift kann nur eine Einheit tragen, deshalb wird hier keine angezeigt.';

const dateFmt = (iso: string): string => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    timeZone: 'UTC',
  });
};

// #2489 — a data-bearing tile with no comparable value ~1 year ago shows "--"
// (neutral) instead of hiding the chip; a real YoY move shows the signed %.
function changeLabel(model: TileModel): string {
  if (model.changePct == null) return '--';
  const sign = model.changePct > 0 ? '+' : '';
  return `${sign}${model.changePct.toFixed(1)} %`;
}

export interface PriceTileGridProps {
  items: TrackedItem[];
  selectedItemId: string | null;
  onSelectItem: (itemId: string) => void;
  /** Resolve a tile's own-price series (#2394). The page owns the lookup so the tile view stays thin. */
  getSeries: (itemId: string) => PriceSeries[];
  /**
   * HOF-270 — resolve a tile's MATERIALISED summary, when it has one.
   *
   * Where it does, the model is built from it and this tile costs O(1) instead of
   * an aggregation over the item's whole history. Where it does not, the tile
   * falls back to `getSeries` and `buildTileModel` — the pre-HOF-270 path,
   * unchanged, because a false „Keine Daten" on the surface a farmer prices from
   * is worse than a slow one (AC 17).
   *
   * The two paths must not diverge: `tests/fixtures/preisspiegel_tile_parity@v1.json`
   * pins byte-exactly that they build the SAME `TileModel` from the same facts,
   * asserted here in `tileFromSummary.test.ts` and in Python where the fact is
   * written. Divergence on this surface is a wrong margin, not a wrong tile
   * (HOF-21 values a Leistung-Posten at these prices).
   *
   * Unpassed — an older caller — means every tile takes the fallback, which is
   * the behaviour that existed before this prop.
   */
  getSummary?: (itemId: string) => PreisspiegelTileSummary | undefined;
  /**
   * HOF-19 / AC 22 (codex r2) — which benchmark series this item holds.
   *
   * A benchmark-only item reaches the grid through the index's has-data fold,
   * and without this its tile rendered "Keine Daten", disabled — visible but
   * dead, which is the AC's failure mode with one extra step. The tile stays
   * price-less (the headline is own-only, AC 8); what changes is the copy and
   * the click: a reference label, openable, and the chart inside carries the
   * line.
   *
   * HOF-248 — the ids, not a boolean. The label used to be the constant
   * "AMI-Referenz", so once a second population existed every tenant without an
   * AMI licence was told its public reference was AMI's. Presence and name now
   * come from the same value and cannot disagree.
   */
  benchmarkSeriesIds?: (itemId: string) => readonly string[];
  /**
   * #2694 — what the price layer can currently say about these tiles.
   *
   * The items here came from the INDEX, so they are known to hold observations.
   * Until the price read has actually answered, a tile with no series is
   * therefore UNKNOWN, not empty — and the two ways of not having answered are
   * different statements to a farmer: ``loading`` is a wait, ``unavailable`` is
   * a failure. Only ``ready`` licenses "Keine Daten".
   *
   * Applied per tile rather than per grid on purpose: it only suppresses the
   * empty state where there is genuinely nothing to show yet, so once the prices
   * land the tiles they did not cover fall back to an honest "Keine Daten"
   * instead of spinning forever. That also survives the per-Kategorie batching
   * this read is being split into, where tiles fill in at different times.
   */
  prices?: PriceAvailability;
  /**
   * #2944 — the units each item's live facts were PUBLISHED under, from the
   * index read (``usePriceIndex().unitsByItem``).
   *
   * The catalog-wide ``/cells`` read cannot state a unit — its group key is at
   * UDB's ceiling — so on the live page the series arrive unlabelled and this is
   * what the headline is labelled from. Absent (an unpassed prop, or an item
   * with no entry) means the tile states no unit rather than falling back to
   * ``item.unit``, which is the substitution that made a €/kg price read as €/l.
   */
  unitsByItem?: Record<string, readonly string[]>;
  /**
   * HOF-377 — the MATERIALISED benchmark presence bit per item, in the drawn
   * vocabulary (`consumer` / `trade`).
   *
   * Preferred over {@link benchmarkSeriesIds} where it answers, and that is the
   * whole saving: the label is the ONLY thing the grid ever used the benchmark
   * gather for, and the gather cost 5-8 UDB aggregates of 2-5 s each per visit.
   * Absent — the `/tiles` fallback path — leaves the series-derived label exactly
   * as it was, so the two paths render the same words (`benchmarkTileLabel` and
   * `benchmarkTileLabelFromLevels` are one rule with two entry points).
   */
  benchmarkLevels?: (itemId: string) => readonly string[];
  /**
   * Whether the 120-day curves have landed (HOF-377 AC 13).
   *
   * `'loading'` is first paint on the split read: the tile has its title, price,
   * *Stand*, delta chip and Quellen count, and the sparkline slot holds a
   * placeholder so the grid does not reflow when the curve arrives — the `#2420`
   * skeleton property, one row down. Defaults to `'ready'`, which is every caller
   * whose points ride the same response as the headline.
   *
   * `'unavailable'` holds the same slot and does NOT shimmer — the distinction the
   * price skeleton one row up already makes: only a read that is actually in
   * progress is in progress. What it must never do is fall through to the empty
   * treatment, which is the page's mark for *this item has no history*.
   */
  sparklines?: 'ready' | 'loading' | 'unavailable';
}

/** What the price layer can say about a tile that has no series yet. */
export type PriceAvailability = 'ready' | 'loading' | 'unavailable';

export default function PriceTileGrid({
  items,
  selectedItemId,
  onSelectItem,
  getSeries,
  getSummary,
  prices = 'ready',
  unitsByItem,
  benchmarkSeriesIds,
  benchmarkLevels,
  sparklines = 'ready',
}: PriceTileGridProps) {
  return (
    <div className="preisspiegel-grid" role="list">
      {items.map((item) => {
        // HOF-270 — the summary where there is one, the live aggregation where
        // there is not. Two builders, one `TileModel`, pinned identical by the
        // parity fixture; from here down nothing knows which path produced it.
        const summary = getSummary?.(item.id);
        const model = summary
          ? tileModelFromSummary(item, summary)
          : buildTileModel(item, getSeries(item.id), unitsByItem?.[item.id]);
        // "No series AND the price layer has not answered" — the cases where
        // absence carries no information. Only a tile whose price read actually
        // came back empty is a real "Keine Daten".
        const unknown = model.state === 'empty' && prices !== 'ready';
        const isPending = unknown && prices === 'loading';
        const isUnavailable = unknown && prices === 'unavailable';
        const isEmpty = model.state === 'empty' && !unknown;
        // Own-empty but benchmark-backed: openable, and says what it holds —
        // which population it holds included (HOF-248).
        // HOF-377 — the MATERIALISED bit first, the fetched series second. The two
        // produce the same string for the same data (one rule, two entry points), so
        // this is a source preference and not a behaviour switch: an item whose
        // summary carries levels needs no benchmark read to be labelled at all.
        const levels = benchmarkLevels?.(item.id) ?? [];
        const benchmarkLabel = isEmpty
          ? (benchmarkTileLabelFromLevels(levels) ?? benchmarkTileLabel(benchmarkSeriesIds?.(item.id) ?? []))
          : null;
        const benchmarkOnly = benchmarkLabel !== null;
        const change = changeLabel(model);
        // #2441 — the Saatgut/Pflanzgut basis split ("je Pflanze" vs "je Einheit" vs "je Gewicht"),
        // so a €/Stk Pflanzgut tile reads distinctly from a €/Einheit seed-unit tile (#2463).
        const basisLabel = model.basis ? PRICE_BASIS_LABELS[model.basis] : null;

        // Price is the hero (scope); the as-of date drops to the small meta row below.
        // The unknown-price copy names the WAIT or the FAILURE, never the
        // absence — it has to be unmistakable from "Keine Daten" when read
        // aloud, not just when seen.
        //
        // #2944 — the unit comes from the MODEL, which took it from the facts.
        // Reading `item.unit` here is what turned a 380,00 €/kg observation into
        // 380,00 €/l the moment the catalog was edited. Where no single unit can
        // be stated the price still renders — it is true — but bare, with the
        // reason spelled out in the footer rather than left as a mystery.
        const price = priceFmt(model.lastPrice as number);
        const scope = isPending
          ? 'Preis wird geladen …'
          : isUnavailable
            ? 'Preis nicht verfügbar'
            : isEmpty
              ? (benchmarkLabel ?? 'Keine Daten')
              : model.unit
                ? `${price} ${model.unit}`
                : price;

        const footer = unknown ? (
          <span className="preisspiegel-tile__footer preisspiegel-tile__footer--pending">
            {/* A bar standing in for the change chip + sparkline row, so the tile
                keeps its height and the grid does not reflow when the real footer
                replaces it (#2420). It only shimmers while we are actually
                waiting — a failed read is not in progress. */}
            <span
              className={`preisspiegel-tile__skeleton${isPending ? ' preisspiegel-tile__skeleton--busy' : ''}`}
              aria-hidden="true"
            />
            {basisLabel && <span className="preisspiegel-tile__basis">{basisLabel}</span>}
          </span>
        ) : isEmpty ? (
          <span className="preisspiegel-tile__footer preisspiegel-tile__footer--empty">
            {basisLabel ? <span className="preisspiegel-tile__basis">{basisLabel}</span> : '—'}
          </span>
        ) : (
          <span className="preisspiegel-tile__footer-wrap">
            <span className="preisspiegel-tile__footer">
              <span className={`preisspiegel-tile__change preisspiegel-tile__change--${model.state}`}>{change}</span>
              {/* HOF-377 AC 13 — first paint draws the headline and holds the
                  sparkline's slot. `Sparkline` already renders a fixed-height
                  element for a curve with no points, so the height is kept either
                  way; what `pending` adds is that the slot SAYS it is waiting
                  rather than reading as a tile that has no history. */}
              <Sparkline
                model={model.sparkline}
                state={model.state}
                pending={sparklines !== 'ready' && !model.sparkline.hasData}
                pendingBusy={sparklines === 'loading'}
              />
            </span>
            <span className="preisspiegel-tile__meta">
              {/* #2777 — FIRST in the meta row, immediately under the price it
                  qualifies. The tile is where the #2695 false-green happened:
                  a curated 38 €/t rendered in the same slot, type and colour as
                  a price a buyer actually paid. `MarketplaceTile.scope` takes a
                  plain string, so the chip cannot ride the price line itself —
                  the next line up, before the Stand it also qualifies, is the
                  closest honest place, and the hatch keeps it legible in
                  greyscale where the tint alone would not be. */}
              {model.estimated && <EstimatedChip />}
              {/* #2944 — the tile holds prices in two units, so the headline
                  above it is deliberately bare. Naming that is not optional
                  polish: a price with no unit and no explanation reads as a
                  rendering bug, and the farmer's next move is to assume the
                  unit they expect — which is exactly the wrong answer this
                  suppression exists to withhold. */}
              {model.unitMixed && (
                <span className="preisspiegel-tile__unit-mixed" title={UNIT_MIXED_HINT}>
                  Einheit uneinheitlich
                </span>
              )}
              {basisLabel && <span className="preisspiegel-tile__basis">{basisLabel}</span>}
              {model.asOf && <span>Stand {dateFmt(model.asOf)}</span>}
              {model.sourceCount > 1 && <span className="preisspiegel-tile__sources">{model.sourceCount} Quellen</span>}
            </span>
          </span>
        );

        // ``--loading``/``--unavailable`` REPLACE ``--empty`` rather than
        // joining it: the two must never both be addressable, or a stylesheet
        // could quietly restore the empty treatment to a tile that is merely
        // waiting.
        const stateClass = isPending ? 'loading' : isUnavailable ? 'unavailable' : model.state;

        return (
          <div
            role="listitem"
            key={item.id}
            // aria-busy ONLY while a read is genuinely in progress. The tile is
            // additionally aria-disabled (via variant='disabled') because it is
            // not clickable in either unknown state; the two say different
            // things — "in progress" vs "not actionable" — and a failed read is
            // the second without being the first.
            {...(isPending ? { 'aria-busy': true as const } : {})}
            className={`preisspiegel-tile-wrap${item.id === selectedItemId ? ' preisspiegel-tile-wrap--selected' : ''} preisspiegel-tile-wrap--${stateClass}`}
          >
            <MarketplaceTile
              icon={
                <span className={`preisspiegel-tile__trend preisspiegel-tile__trend--${stateClass}`}>
                  {isPending ? <Loader2 size={16} aria-hidden="true" /> : TREND_ICON[isUnavailable ? 'empty' : model.state]}
                </span>
              }
              title={item.name}
              scope={scope}
              footer={footer}
              variant={(isEmpty && !benchmarkOnly) || unknown ? 'disabled' : 'available'}
              onClick={(isEmpty && !benchmarkOnly) || unknown ? undefined : () => onSelectItem(item.id)}
              testId={`preisspiegel-tile-${item.id}`}
            />
          </div>
        );
      })}
    </div>
  );
}
